/* Quotes the existing cart using Shopify's native rates. Never adds, removes,
   or alters an item. No address is stored in browser storage or analytics. */
(() => {
  if (customElements.get('lb-shipping-estimator')) return;
  class LBShippingEstimator extends HTMLElement {
    connectedCallback() {
      this.listeners?.abort();
      this.listeners = new AbortController();
      const {signal} = this.listeners;
      this.form = this.querySelector('[data-shipping-form]');
      this.country = this.querySelector('[data-country]');
      this.province = this.querySelector('[data-province]');
      this.zip = this.querySelector('[data-zip]');
      this.status = this.querySelector('[data-status]');
      this.rates = this.querySelector('[data-rates]');
      this.submit = this.form.querySelector('button[type="submit"]');
      this.messages = JSON.parse(this.querySelector('[data-shipping-messages]').textContent);
      this.country.value = this.country.dataset.default;
      if (!this.country.value && typeof Intl.DisplayNames === 'function') {
        this.country.value = new Intl.DisplayNames(['en'], {type:'region'}).of(this.dataset.countryCode);
      }
      this.updateProvinces();
      this.country.addEventListener('change', () => { this.updateProvinces(); this.invalidate(); }, {signal});
      this.province.addEventListener('change', () => this.invalidate(), {signal});
      this.zip.addEventListener('input', () => this.invalidate(), {signal});
      this.form.addEventListener('submit', event => { event.preventDefault(); this.calculate(); }, {signal});
      if (typeof subscribe === 'function') {
        this.unsubscribe?.();
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.invalidate());
      }
      window.addEventListener('pageshow', event => { if (event.persisted) this.invalidate(); }, {signal});
      document.addEventListener('visibilitychange', () => { if (!document.hidden) this.invalidate(); }, {signal});
    }
    disconnectedCallback() {
      this.listeners?.abort(); this.unsubscribe?.(); this.invalidate(false);
    }
    updateProvinces() {
      let provinces = [];
      try { provinces = JSON.parse(this.country.selectedOptions[0]?.dataset.provinces || '[]'); } catch (_) { /* Invalid country metadata: do not guess. */ }
      this.province.replaceChildren();
      for (const [value, label] of provinces) {
        const option = document.createElement('option'); option.value = value; option.textContent = label;
        this.province.append(option);
      }
      this.querySelector('[data-province-wrap]').hidden = provinces.length === 0;
      this.province.disabled = provinces.length === 0;
    }
    invalidate(announce = true) {
      const hadQuote = this.hasQuote || this.busy;
      this.request?.abort(); this.request = null;
      this.hasQuote = false; this.busy = false;
      if (!this.rates) return;
      this.rates.replaceChildren(); this.rates.hidden = true;
      this.submit.disabled = false; this.removeAttribute('aria-busy');
      this.status.textContent = announce && hadQuote ? this.messages.changed : '';
    }
    endpoint(path, params) {
      const root = window.Shopify?.routes?.root || this.dataset.root || '/';
      const url = new URL(root.replace(/\/?$/, '/') + path, window.location.origin);
      if (params) url.search = params.toString();
      return url;
    }
    async json(path, signal, params, method = 'GET') {
      const response = await fetch(this.endpoint(path, params), {method, signal, credentials: 'same-origin', cache: 'no-store', headers: {Accept: 'application/json'}});
      if (!response.ok) throw new Error('Shipping request failed');
      return response.json();
    }
    fingerprint(cart) {
      return JSON.stringify([cart.currency, cart.total_price, cart.total_weight, cart.requires_shipping, cart.items.map(item => [item.key, item.quantity, item.final_line_price])]);
    }
    pause(ms, signal) {
      return new Promise((resolve, reject) => {
        const aborted = () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); };
        const timer = setTimeout(() => { signal.removeEventListener('abort', aborted); resolve(); }, ms);
        if (signal.aborted) aborted(); else signal.addEventListener('abort', aborted, {once:true});
      });
    }
    async calculate() {
      if (this.busy) return;
      this.invalidate(false);
      if (!this.country.value) { this.status.textContent = this.messages.invalid; return; }
      this.busy = true; this.submit.disabled = true; this.setAttribute('aria-busy', 'true');
      const request = this.request = new AbortController(); const {signal} = request;
      // Bound requests and polling, including a stalled network response.
      const timeout = setTimeout(() => request.abort('timeout'), 25000);
      this.status.textContent = this.messages.loading;
      try {
        const cart = await this.json('cart.js', signal);
        const productForm = document.getElementById(this.dataset.productForm);
        const variant = productForm?.querySelector('[name="id"]')?.value;
        if (!cart.items?.length || (variant && !cart.items.some(item => String(item.variant_id) === String(variant)))) {
          this.status.textContent = variant ? this.messages.add_product : this.messages.empty;
          return;
        }
        if (cart.requires_shipping === false) { this.status.textContent = this.messages.no_shipping; return; }
        const before = this.fingerprint(cart);
        const params = new URLSearchParams({'shipping_address[country]':this.country.value, 'shipping_address[zip]':this.zip.value.trim()});
        if (!this.province.disabled && this.province.value) params.set('shipping_address[province]', this.province.value);
        await this.json('cart/prepare_shipping_rates.json', signal, params, 'POST');
        let result = null;
        for (let attempt = 0; attempt < 9; attempt++) {
          if (attempt) await this.pause(Math.min(500 * attempt, 2000), signal);
          result = await this.json('cart/async_shipping_rates.json', signal, params);
          if (result !== null) break;
        }
        if (!result || !Array.isArray(result.shipping_rates)) throw new Error('No shipping response');
        const after = await this.json('cart.js', signal);
        if (before !== this.fingerprint(after)) { this.status.textContent = this.messages.changed; return; }
        if (!result.shipping_rates.length) { this.status.textContent = this.messages.none; return; }
        // Validate every rate first, so a malformed response cannot leave a partial quote.
        const rows = result.shipping_rates.map(rate => {
          const amount = String(rate.price ?? '').trim();
          if (!/^\d+(?:\.\d+)?$/.test(amount) || !Number.isFinite(Number(amount))) throw new Error('Invalid rate');
          const currency = rate.currency || after.currency;
          if (!/^[A-Z]{3}$/.test(currency || '')) throw new Error('Missing currency');
          const money = new Intl.NumberFormat(this.dataset.locale || 'fr', {style:'currency', currency, currencyDisplay:'code'}).format(Number(amount));
          return [String(rate.name || this.messages.results), money];
        });
        if (signal.aborted) return;
        for (const [name, money] of rows) {
          const li = document.createElement('li'); const label = document.createElement('span'); const price = document.createElement('strong');
          label.textContent = name; price.textContent = money; li.append(label, price); this.rates.append(li);
        }
        this.hasQuote = true; this.rates.hidden = false; this.status.textContent = this.messages.results;
      } catch (error) {
        if (!signal.aborted || signal.reason === 'timeout') this.status.textContent = this.messages.error;
      } finally {
        clearTimeout(timeout);
        if (this.request === request) {
          this.busy = false; this.submit.disabled = false; this.removeAttribute('aria-busy'); this.request = null;
        }
      }
    }
  }
  customElements.define('lb-shipping-estimator', LBShippingEstimator);
})();
